/**
 * report/tableMapping.ts — de 17 tabellen uit het format, met per kolom de bron in GeoVoice en de
 * opmaakregel (featureplan §16). Een letterlijke kopie van docs/report-table-mapping.json: die
 * blijft de specificatie, en een test (report-foundation.test.mjs) bewaakt dat ze gelijk blijven.
 *
 * In de editor toont het databronpaneel hieruit per kolom waar de waarde vandaan komt; in fase 2
 * is het de opdracht voor buildReportTables.
 */

export interface ReportTableSpec {
  /** Tabelnummer in het format (.1 t/m .16); null voor de pakkettabel. */
  n: number | null;
  /** Paragraaf (basisnummering) waar de tabel staat. */
  sec: string;
  title: string;
  /** Hoe de rijen zijn ingedeeld. */
  group: string;
  /** De belangrijkste bronnen. */
  main: string;
  /** Per kolom: [kolomkop, bron, opmaakregel]. */
  cols: [string, string, string][];
}

/** De sleutel waarmee REPORT_OUTLINE naar een tabel verwijst: 't6', of 'tPackages'. */
export const reportTableKey = (t: { n: number | null }): string => (t.n == null ? 'tPackages' : `t${t.n}`);

export const REPORT_TABLE_MAPPING: ReportTableSpec[] = [
  {
    "n": 1,
    "sec": "1.1",
    "title": "Onderzoeksresultaten",
    "group": "1 rij per deellocatie",
    "main": "sublocations · toets_results · labresultaten · reports.facts",
    "cols": [
      [
        "Locatie",
        "sublocations.name + description",
        "\"Deellocatie 1 - {omschrijving}\""
      ],
      [
        "Bodemkwaliteit",
        "toets_results (matrix soil) van alle analysemonsters in de deellocatie",
        "Zwaarste judgement. aboveIntervention → \"Voldoet niet aan de interventiewaarde bodemkwaliteit\", anders \"Geen overschrijdingen interventiewaarde bodemkwaliteit. Geen nader onderzoek nodig\""
      ],
      [
        "Kwaliteitsklasse bodem",
        "toets_results.quality_class",
        "Zwaarste klasse over de mengmonsters grond van de deellocatie"
      ],
      [
        "Toepassingsklasse PFAS",
        "toets_results.pfas_class",
        "Zwaarste klasse over de PFAS-monsters"
      ],
      [
        "Grondwater",
        "labresultaten van watermonsters uit peilbuizen in de deellocatie + toets_results (matrix groundwater)",
        "Geen overschrijding → \"Geen overschrijding van de signaleringswaarden\"; anders \"Overschrijding van de {niveau} voor {stoffen}\""
      ],
      [
        "Asbest in bodem",
        "labresultaat asbest totaal van analysemonsters sample_kind=asbestos, matrix=soil",
        "Niet aangetoond → \"Geen asbest\"; anders hoogste gehalte \"{x} mg/kg d.s.\""
      ],
      [
        "Asbest in puinfundering",
        "idem, matrix=rubble",
        ""
      ],
      [
        "PAK in asfalt",
        "labresultaat PAK10 van asfaltmonsters",
        "< 75 mg/kg d.s. → \"Teervrij\", ≥ 75 → \"Teerhoudend\" (CROW 210)"
      ],
      [
        "Voorlopige CROW 400 veiligheidsklasse",
        "reports.facts.crow400[sublocationId]",
        "Afgeleid uit toetsing en asbest, adviseur bevestigt (open punt 5)"
      ]
    ]
  },
  {
    "n": 2,
    "sec": "1.2.3",
    "title": "Verplichtingen graven",
    "group": "2 vaste rijen",
    "main": "company_report_texts · toets_results",
    "cols": [
      [
        "Bodemkwaliteit",
        "vaste tekst",
        ""
      ],
      [
        "Deellocatie",
        "sublocations waarvan de zwaarste judgement aboveIntervention is (rij 1) of niet (rij 2)",
        "Namen kommagescheiden; rij zonder deellocaties vervalt"
      ],
      [
        "Meer dan 25 m³ ontgraven / Tijdelijk uitnemen / Overig",
        "company_report_texts",
        "Vaste teksten, per bureau te beheren"
      ]
    ]
  },
  {
    "n": 3,
    "sec": "3.2",
    "title": "Onderzoekslocatie",
    "group": "1 rij per deellocatie",
    "main": "sublocations",
    "cols": [
      [
        "Locatie",
        "sublocations.description",
        ""
      ],
      [
        "Oppervlakte (m2)",
        "sublocations.area_m2",
        "\"{x} m2\""
      ]
    ]
  },
  {
    "n": 4,
    "sec": "3.3.1",
    "title": "Onderzoeksinspanning",
    "group": "1 rij per deellocatie",
    "main": "borings · peilbuizen · lab_analysis_samples",
    "cols": [
      [
        "Locatie",
        "sublocations.name + description",
        ""
      ],
      [
        "Boringen",
        "borings (findActiveByProject, type ≠ IB) met boring_sublocations",
        "Groeperen op einddiepte = max(layers.lowerDepth): \"5 x tot 1,5 m-mv\" per regel"
      ],
      [
        "Waarvan met peilbuis",
        "peilbuizen.boringId ∈ boringen van de deellocatie",
        "\"{n} x\""
      ],
      [
        "Proefgaten asbest",
        "borings.type = IB (inspectiegat)",
        "\"{n} x\""
      ],
      [
        "Analyses grond (kolomkoppen)",
        "lab_analysis_samples.packages → package_name",
        "Eén kolom per uniek pakket bij matrix soil"
      ],
      [
        "Analyses grond (aantallen)",
        "lab_analysis_samples matrix=soil, via parts → boring_sublocations",
        "Aantal analysemonsters met dat pakket: \"{n} x\""
      ],
      [
        "Analyses grondwater",
        "lab_analysis_samples matrix=water, of watermonsters met analysisPackage",
        "\"{n} x\""
      ],
      [
        "Analyses puinfundering",
        "lab_analysis_samples matrix=rubble",
        "\"{n} x\" per pakket"
      ]
    ]
  },
  {
    "n": null,
    "sec": "3.3.1",
    "title": "Analysepakketten",
    "group": "1 rij per gebruikt pakket",
    "main": "lab_package_catalog · company_report_texts",
    "cols": [
      [
        "Pakketnaam",
        "unieke packages[].name over alle analysemonsters van het rapport",
        ""
      ],
      [
        "Omschrijving",
        "company_report_texts (key package.{code}) of lab_package_catalog.suite_json",
        "Valt terug op de componentenlijst uit de SGS-suite"
      ]
    ]
  },
  {
    "n": 5,
    "sec": "3.3.2",
    "title": "Asfalt onderzoeksinspanning",
    "group": "1 rij per deellocatie met asfalt",
    "main": "sublocations · borings",
    "cols": [
      [
        "Locatie",
        "sublocations.description",
        ""
      ],
      [
        "Oppervlakte in m2",
        "sublocations.area_m2",
        "\"circa {x} m2\""
      ],
      [
        "Aantal asfaltboringen",
        "borings met minstens één laag met bzb.code asfalt",
        ""
      ]
    ]
  },
  {
    "n": 6,
    "sec": "4.2.2",
    "title": "Analyseresultaten grond",
    "group": "groepsrij per deellocatie, dan 1 rij per mengmonster",
    "main": "lab_analysis_samples · lab_analysis_sample_parts · monsters · layers · toets_results",
    "cols": [
      [
        "Deellocatie (groepsrij)",
        "boring_sublocations van de parts",
        "Alle parts van één mengmonster horen in één deellocatie, anders validatiefout"
      ],
      [
        "Analysemonster",
        "lab_analysis_samples.name",
        "\"MBZ01\""
      ],
      [
        "Deelmonsters (m-mv)",
        "parts → monsters.name + parts.upperDepth/lowerDepth (cm)",
        "Per part een regel: \"301 (0,0 – 0,5)\". cm/100, één decimaal, komma"
      ],
      [
        "Hoofdgrondsoort en zintuiglijke waarneming",
        "parts.layer_id → layers.soilType, toevoeging1-3/gradatie1-3, bzb[], geurType, oilWaterReaction",
        "getSoilLabel + formatSoilString; bereik over parts: \"Zand, zwak tot matig baksteen\""
      ],
      [
        "Analysepakket",
        "lab_analysis_samples.packages[].name",
        "Kommagescheiden"
      ],
      [
        "Wonen / Industrie / Matig / Sterk (>I)",
        "toets_results.exceedances_json gefilterd op level",
        "Stofnamen per niveau, \"-\" als leeg"
      ],
      [
        "Toetsoordeel",
        "toets_results.quality_class",
        "\"Klasse industrie\""
      ],
      [
        "Veiligheidsklasse",
        "reports.facts.crow400 per analysemonster",
        ""
      ]
    ]
  },
  {
    "n": 7,
    "sec": "4.2.3",
    "title": "Analyseresultaten PFAS",
    "group": "1 rij per PFAS-analysemonster",
    "main": "lab_analysis_samples · parts · labresultaten · toets_results",
    "cols": [
      [
        "Locatie",
        "boring_sublocations van de parts",
        ""
      ],
      [
        "Analysemonster",
        "lab_analysis_samples.name waar packages een PFAS-pakket bevat",
        ""
      ],
      [
        "Deelmonsters (m-mv)",
        "parts → monsters.name + diepte",
        "Zelfde opmaak als tabel .6"
      ],
      [
        "Hoofdgrondsoort en zintuiglijke waarneming",
        "parts → layers",
        "Zelfde als tabel .6"
      ],
      [
        "Analysepakket",
        "packages[].name",
        ""
      ],
      [
        "Gehalten PFAS >L/N (µg/kg d.s.)",
        "toets_results.exceedances_json level aboveBackground",
        "\"PFOA: 2,1 µg/kg d.s.\", \"-\" als leeg"
      ],
      [
        "Toepassingsklasse",
        "toets_results.pfas_class",
        ""
      ]
    ]
  },
  {
    "n": 8,
    "sec": "4.3.1",
    "title": "Veldmetingen grondwater",
    "group": "1 rij per watermonster",
    "main": "peilbuizen · borings.watermonsters",
    "cols": [
      [
        "Locatie",
        "boring_sublocations van de boring met de peilbuis",
        ""
      ],
      [
        "Peilbuis",
        "peilbuizen.name",
        ""
      ],
      [
        "Filterdiepte (m–mv)",
        "peilbuizen.filterTopCm / filterBottomCm",
        "\"2,0 – 3,0\""
      ],
      [
        "Grondwaterstand (m-mv)",
        "watermonster.gwsBeforeSamplingCm, anders borings.gwsCmMv",
        "Twee decimalen"
      ],
      [
        "pH",
        "watermonster.ph",
        ""
      ],
      [
        "EC (µS/cm)",
        "watermonster.ec",
        ""
      ],
      [
        "Troebelheid (FTU)",
        "watermonster.turbiditySample",
        ""
      ]
    ]
  },
  {
    "n": 9,
    "sec": "4.3.2",
    "title": "Analyseresultaten grondwater",
    "group": "1 rij per watermonster",
    "main": "peilbuizen · lab_analysis_samples (matrix water) · toets_results",
    "cols": [
      [
        "Locatie",
        "boring_sublocations",
        ""
      ],
      [
        "Peilbuis",
        "peilbuizen.name",
        ""
      ],
      [
        "Diepte filterstelling (m-mv)",
        "peilbuizen.filterTopCm / filterBottomCm",
        ""
      ],
      [
        "Analysepakket",
        "packages[].name of bottles[].analysisPackage",
        ""
      ],
      [
        "Overschrijding rapportagegrens",
        "labresultaten: meetwaarde zonder \"<\"-teken",
        "Stofnamen"
      ],
      [
        "Overschrijding signaleringswaarde (index)",
        "toets_results.exceedances_json",
        "\"Arseen (1,36)\""
      ]
    ]
  },
  {
    "n": 10,
    "sec": "4.4.4",
    "title": "Asbest in bodem: gehalte",
    "group": "1 rij per asbest-analysemonster",
    "main": "lab_analysis_samples (asbestos, soil) · parts · borings · labresultaten",
    "cols": [
      [
        "Locatie",
        "boring_sublocations",
        ""
      ],
      [
        "Analysemonster",
        "lab_analysis_samples.name",
        "\"ASB01\""
      ],
      [
        "Deelmonsters",
        "parts → monsters.name + diepte",
        "\"301 (0,0 – 0,5)\""
      ],
      [
        "Gewicht monster fijne fractie <20 mm (kg)",
        "som van borings.massLt20Kg over de boringen van de parts",
        "Twee decimalen"
      ],
      [
        "Gemeten gehalte fractie >20 / <20 mm",
        "labresultaat per fractie (componentcode SGS, open punt)",
        "\"<2\" blijft tekst, zie sikb-labresultaat.js"
      ],
      [
        "Asbestgehalte totaal (mg/kg d.s.)",
        "labresultaat gewogen totaal",
        ""
      ]
    ]
  },
  {
    "n": 11,
    "sec": "4.4.4",
    "title": "Asbest in bodem: type",
    "group": "1 rij per asbest-analysemonster",
    "main": "labresultaten",
    "cols": [
      [
        "Locatie / Analysemonster / Deelmonsters",
        "zoals tabel .10",
        ""
      ],
      [
        "Type materiaal",
        "labresultaat valueText per aangetroffen materiaal",
        "Eén regel per materiaal"
      ],
      [
        "Type asbest",
        "labresultaat valueText",
        "\"Chrysotiel (15-30%)\""
      ],
      [
        "Hechtgebonden",
        "labresultaat",
        "Ja / Nee"
      ]
    ]
  },
  {
    "n": 12,
    "sec": "4.5.4",
    "title": "Asbest in puinfundering: gehalte",
    "group": "1 rij per analysemonster",
    "main": "lab_analysis_samples (asbestos, rubble) · parts · borings",
    "cols": [
      [
        "Alle kolommen",
        "zoals tabel .10, met matrix=rubble",
        "Puinlagen herkend aan bzb.code puin"
      ]
    ]
  },
  {
    "n": 13,
    "sec": "4.5.4",
    "title": "Asbest in puinfundering: type",
    "group": "1 rij per analysemonster",
    "main": "labresultaten",
    "cols": [
      [
        "Alle kolommen",
        "zoals tabel .11, met matrix=rubble",
        ""
      ]
    ]
  },
  {
    "n": 14,
    "sec": "4.5.5",
    "title": "Uitloging en samenstelling puinfundering",
    "group": "1 rij per analysemonster",
    "main": "lab_analysis_samples (rubble, pakket uitloging) · toets_results",
    "cols": [
      [
        "Locatie / Analysemonster / Deelmonsters",
        "zoals tabel .10",
        ""
      ],
      [
        "Overschrijding maximale concentratiewaarde",
        "toets_results.exceedances_json (samenstelling)",
        "Stofnamen, \"-\" als leeg"
      ],
      [
        "Overschrijding maximale emissiewaarde (mg/m2)",
        "toets_results.exceedances_json (uitloging)",
        ""
      ]
    ]
  },
  {
    "n": 15,
    "sec": "4.6.1",
    "title": "Asfaltkernen",
    "group": "1 rij per asfaltlaag, kernboring samengevoegd",
    "main": "borings · layers (bzb asfalt) · lab_analysis_sample_parts",
    "cols": [
      [
        "Locatie",
        "boring_sublocations",
        "rowspan over alle kernen van de deellocatie"
      ],
      [
        "Kernboring",
        "borings.name",
        "rowspan over de lagen"
      ],
      [
        "Aantal asfaltlagen",
        "aantal layers met bzb.code asfalt",
        ""
      ],
      [
        "Gewicht boorkern in gram",
        "nieuw veld borings.coreMassG",
        "Ontbreekt nog in het model"
      ],
      [
        "Diepte per asfaltlaag (cm)",
        "layers.upperDepth – lowerDepth",
        "\"0 – 4,9\""
      ],
      [
        "Soort asfalt",
        "layers.bzb[].asphaltType",
        "\"DAB 0-11\""
      ],
      [
        "Analysemonster asfaltkern",
        "parts.layer_id → lab_analysis_samples.name",
        ""
      ],
      [
        "Lagen in analysemonster",
        "parts.upperDepth/lowerDepth",
        "\"Laag 0 – 4,9\""
      ]
    ]
  },
  {
    "n": 16,
    "sec": "4.6.3",
    "title": "PAK10 asfalt",
    "group": "1 rij per asfalt-analysemonster",
    "main": "lab_analysis_samples · labresultaten",
    "cols": [
      [
        "Locatie",
        "boring_sublocations",
        ""
      ],
      [
        "Analysemonster",
        "lab_analysis_samples.name",
        ""
      ],
      [
        "Gehalte PAK10 (mg/kg d.s.)",
        "labresultaat componentcode PAK10",
        "Tekst behouden (\"<10\")"
      ]
    ]
  }
];

/** De tabel bij een sleutel uit REPORT_OUTLINE, of undefined. */
export const reportTableSpec = (key: string): ReportTableSpec | undefined =>
  REPORT_TABLE_MAPPING.find((t) => reportTableKey(t) === key);
